import { Button, Icon, Input, InputV2 } from "@southfields-digital/mxmz-react-ui";
import classNames from "classnames";
import { useEffect, useState } from "react";

import { type Member, MemberTypeOptions, type MemberTypes } from "src/types/matchControl/member";

import type { MemberColumn, NumericMemberColumnKey, TextMemberColumnKey } from "./memberColumns";

type Props = {
	member: Member;
	columns: readonly MemberColumn[];
	handleUpdateMember: (member: Member) => void;
	handleDeleteMember: () => void;
};

const MemberRow = ({ member, columns, handleUpdateMember, handleDeleteMember }: Props) => {
	const [localMember, setLocalMember] = useState(member);
	const [numberDisplayValue, setNumberDisplayValue] = useState(member.number?.toString() || "");

	useEffect(() => {
		setLocalMember(member);
		setNumberDisplayValue(member.number?.toString() || "");
	}, [member]);

	const updateMember = (updatedMember: Member) => {
		setLocalMember(updatedMember);
		handleUpdateMember(updatedMember);
	};

	const handleChangeText =
		(field: TextMemberColumnKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
			if (field === "number") setNumberDisplayValue(e.target.value);
			updateMember({ ...localMember, [field]: e.target.value });
		};

	const handleChangeType = (e: React.ChangeEvent<HTMLSelectElement>) => {
		updateMember({ ...localMember, type: e.target.value as MemberTypes });
	};

	const handleChangeNumeric =
		(field: NumericMemberColumnKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value === "" ? undefined : parseInt(e.target.value, 10);
			updateMember({ ...localMember, [field]: value });
		};

	const handleNumberBlur = () => {
		// If the field is empty when user leaves it, reset to the current member number
		if (numberDisplayValue === "") {
			setNumberDisplayValue(localMember.number?.toString() || "");
		}
	};

	// Column order is driven by the active preset (the shared source of truth), so
	// the row editor and the spreadsheet paste parser always present columns identically.
	const renderColumn = (column: MemberColumn) => {
		switch (column.type) {
			case "select":
				return (
					<InputV2
						key={column.key}
						id="type"
						type="select"
						inputSize="lg"
						value={localMember.type}
						options={MemberTypeOptions}
						onChange={handleChangeType}
						wrapperClassName={classNames(
							column.className,
							// TODO: REMOVE THIS STYLING OVERRIDE WHEN ALL INPUTS USE INPUTV2
							"!min-h-[42px] !rounded-[4px] !bg-elevated-0 !border-gray-800",
						)}
					/>
				);
			case "number":
				return (
					<Input
						key={column.key}
						type="number"
						value={localMember[column.key as NumericMemberColumnKey]?.toString() || ""}
						onChange={handleChangeNumeric(column.key as NumericMemberColumnKey)}
						placeholder={column.placeholder || column.label}
						min={0}
						max={column.key === "age" ? 99 : undefined}
						className={column.className}
					/>
				);
			default: {
				const field = column.key as TextMemberColumnKey;
				// The shirt number keeps its own display value so clearing the field
				// does not immediately reset while the operator is still typing.
				const isNumberField = field === "number";

				return (
					<Input
						key={column.key}
						type="text"
						value={isNumberField ? numberDisplayValue : localMember[field] || ""}
						onChange={handleChangeText(field)}
						onBlur={isNumberField ? handleNumberBlur : undefined}
						placeholder={column.placeholder || column.label}
						className={column.className}
					/>
				);
			}
		}
	};

	return (
		<div className="flex flex-row gap-2 items-center px-2 py-1">
			<Icon icon="sort" weight="bold" className="text-[#5B5D70]" />
			{columns.map(renderColumn)}
			<Button variant="danger" onClick={handleDeleteMember}>
				<Icon icon="trash" />
			</Button>
		</div>
	);
};

export default MemberRow;
